import { useState, useEffect } from "react";
import { RefreshCw, Play, X, CheckCircle, AlertCircle, ChevronRight, Zap, Copy, Check } from "lucide-react";
import { fetchScreenLatest, triggerScreen, openTrade, analyzeOption } from "../api";
import type { ScreenResult, Recommendation, Strategy, OptionLeg } from "../api";
import { useAuth } from "../AuthContext";

// ─── Helpers ───────────────────────────────────────────────────────────────

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatStrategyLabel(s: Strategy): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function strategyBadgeStyle(s: Strategy): React.CSSProperties {
  if (s === "bull_put_spread" || s === "sell_put") {
    return { background: "#052e16", border: "1px solid #166534", color: "#4ade80" };
  }
  if (s === "bear_call_spread" || s === "sell_call") {
    return { background: "#1a0a0a", border: "1px solid #7f1d1d", color: "#f87171" };
  }
  if (s === "buy_call") {
    return { background: "#0a1a2e", border: "1px solid #1d4ed8", color: "#60a5fa" };
  }
  if (s === "buy_put") {
    return { background: "#1a0a20", border: "1px solid #7e22ce", color: "#c084fc" };
  }
  return { background: "#0d1526", border: "1px solid #1e293b", color: "#94a3b8" };
}

function isSingleLeg(s: Strategy): boolean {
  return s === "sell_put" || s === "sell_call" || s === "buy_call" || s === "buy_put";
}

function isBuyStrategy(s: Strategy): boolean {
  return s === "buy_call" || s === "buy_put";
}

function isSpreadStrategy(s: Strategy): boolean {
  return s === "bull_put_spread" || s === "bear_call_spread";
}

function formatStrategyFriendly(s: Strategy): string {
  switch (s) {
    case "sell_put": return "Cash-Secured Put";
    case "sell_call": return "Covered Call";
    case "buy_call": return "Long Call";
    case "buy_put": return "Long Put";
    default: return formatStrategyLabel(s);
  }
}

function formatExpiry(dateStr: string): string {
  // Parse as UTC to avoid timezone shift (e.g. "2026-04-10" midnight UTC → Apr 9 in US timezones)
  const [y, m, d] = dateStr.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  return utc.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals);
}

// ─── Robinhood order builder ────────────────────────────────────────────────

function buildRobinhoodOrder(rec: Recommendation): { instruction: string; detail: string } {
  const isPut = rec.strategy.includes("put");
  const optType = isPut ? "Put" : "Call";
  const expiry = formatExpiry(rec.short_expiry);

  if (isSpreadStrategy(rec.strategy)) {
    const limitCredit = (rec.net_premium * 0.95).toFixed(2);
    const perContract = Math.round(parseFloat(limitCredit) * 100);
    const spreadType = isPut ? "Bull Put Spread" : "Bear Call Spread";
    return {
      instruction: `SELL $${rec.short_strike}${isPut ? "P" : "C"} / BUY $${rec.long_strike}${isPut ? "P" : "C"} · ${rec.ticker} · Exp ${expiry} · ${rec.dte} DTE`,
      detail: `${spreadType} · Limit credit $${limitCredit}/share · $${perContract}/contract · Breakeven $${fmt(rec.breakeven)} ($${fmt(rec.net_premium)})`,
    };
  }

  if (isBuyStrategy(rec.strategy)) {
    const limitDebit = (rec.net_premium * 1.05).toFixed(2); // pay up to 5% more
    const perContract = Math.round(parseFloat(limitDebit) * 100);
    return {
      instruction: `BUY TO OPEN: ${rec.ticker} $${rec.short_strike} ${optType} · Exp ${expiry} · ${rec.dte} DTE`,
      detail: `${formatStrategyFriendly(rec.strategy)} · Limit debit $${limitDebit}/share · $${perContract}/contract`,
    };
  }

  // sell_put / sell_call
  const limitCredit = (rec.net_premium * 0.95).toFixed(2);
  const perContract = Math.round(parseFloat(limitCredit) * 100);
  const secured = rec.strategy === "sell_put" ? "Cash-secured" : "Covered";
  return {
    instruction: `SELL TO OPEN: ${rec.ticker} $${rec.short_strike} ${optType} · Exp ${expiry} · ${rec.dte} DTE`,
    detail: `${formatStrategyFriendly(rec.strategy)} · Limit $${limitCredit}/share · $${perContract}/contract · ${secured} · Breakeven $${fmt(rec.breakeven)} ($${fmt(rec.net_premium)})`,
  };
}

// ─── Copy button ────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleCopy}
      title="Copy order to clipboard"
      style={{
        background: "transparent", border: "none",
        color: copied ? "#4ade80" : "#475569",
        cursor: "pointer", padding: "2px 4px", borderRadius: 4,
        display: "flex", alignItems: "center", transition: "color 0.15s",
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

// ─── Score bar ─────────────────────────────────────────────────────────────

function ScoreBar({ score }: { score: number }) {
  const pct = Math.min(100, Math.max(0, score));
  const filled = Math.round(pct / 10);
  const empty = 10 - filled;
  const color = pct >= 70 ? "#4ade80" : pct >= 45 ? "#facc15" : "#f87171";
  return (
    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, color, letterSpacing: 1 }}>
      {"█".repeat(filled)}{"░".repeat(empty)}
    </span>
  );
}

// ─── Sentiment badge ──────────────────────────────────────────────────────

function SentimentBadge({ label }: { label: string }) {
  const parts = label.split(" · ");
  const primary = parts[0]; // "Bullish", "Bearish", "Neutral", "Mixed"
  const warnings = parts.slice(1); // "Earnings", "High Risk"

  const colorMap: Record<string, { bg: string; border: string; color: string }> = {
    Bullish: { bg: "#052e16", border: "#166534", color: "#4ade80" },
    Bearish: { bg: "#1a0a0a", border: "#7f1d1d", color: "#f87171" },
    Mixed:   { bg: "#1a1a0a", border: "#854d0e", color: "#facc15" },
    Neutral: { bg: "#0d1526", border: "#1e293b", color: "#94a3b8" },
  };
  const style = colorMap[primary] ?? colorMap.Neutral;

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <span style={{
        fontSize: 10, fontFamily: "monospace", fontWeight: 700,
        padding: "2px 8px", borderRadius: 5,
        background: style.bg, border: `1px solid ${style.border}`, color: style.color,
      }}>
        {primary.toUpperCase()}
      </span>
      {warnings.map((w) => (
        <span key={w} style={{
          fontSize: 9, fontFamily: "monospace", fontWeight: 700,
          padding: "2px 6px", borderRadius: 5,
          background: w === "Earnings" ? "#1a1a0a" : "#1a0a0a",
          border: `1px solid ${w === "Earnings" ? "#854d0e" : "#7f1d1d"}`,
          color: w === "Earnings" ? "#fb923c" : "#f87171",
        }}>
          {w.toUpperCase()}
        </span>
      ))}
    </div>
  );
}

// ─── ExecuteModal ──────────────────────────────────────────────────────────

interface ExecuteModalProps {
  rec: Recommendation;
  onClose: () => void;
  onSuccess: () => void;
}

function ExecuteModal({ rec, onClose, onSuccess }: ExecuteModalProps) {
  const isSpread = rec.long_strike !== null && isSpreadStrategy(rec.strategy);
  const isBuy = isBuyStrategy(rec.strategy);
  const [shortFill, setShortFill] = useState(rec.short_premium.toFixed(2));
  const [longFill, setLongFill] = useState(rec.long_premium != null ? rec.long_premium.toFixed(2) : "");
  const [quantity, setQuantity] = useState("1");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleExecute() {
    setLoading(true);
    setError(null);
    try {
      await openTrade({
        rec_id: rec.id,
        ticker: rec.ticker,
        strategy: rec.strategy,
        short_strike: rec.short_strike,
        short_expiry: rec.short_expiry,
        short_fill: parseFloat(shortFill),
        ...(isSpread && rec.long_strike != null ? {
          long_strike: rec.long_strike,
          long_expiry: rec.long_expiry ?? rec.short_expiry,
          long_fill: parseFloat(longFill),
        } : {}),
        quantity: parseInt(quantity),
        opened_spot: rec.spot_price,
      });
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to open trade");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(5,12,24,0.85)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#0a1628", border: "1px solid #1e293b", borderRadius: 16,
        padding: 32, width: "100%", maxWidth: 480,
        animation: "slideUp 0.2s ease",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <Zap size={16} color="#facc15" />
              <span style={{ fontSize: 11, color: "#facc15", fontFamily: "monospace", letterSpacing: 1 }}>EXECUTE TRADE</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Space Mono', monospace", color: "#f1f5f9" }}>
              {rec.ticker}
            </div>
            <div style={{
              display: "inline-block", marginTop: 6,
              padding: "3px 10px", borderRadius: 6, fontSize: 11, fontFamily: "monospace",
              ...strategyBadgeStyle(rec.strategy),
            }}>
              {(isSingleLeg(rec.strategy) ? formatStrategyFriendly(rec.strategy) : formatStrategyLabel(rec.strategy)).toUpperCase()}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: "#475569",
            cursor: "pointer", padding: 4, borderRadius: 6,
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Strike info */}
        <div style={{
          background: "#0d1526", border: "1px solid #1e293b", borderRadius: 10,
          padding: "12px 16px", marginBottom: 20, fontSize: 13, fontFamily: "monospace",
          color: "#94a3b8", lineHeight: 1.8,
        }}>
          <div>{isBuy ? "Strike" : "Short"}: <span style={{ color: "#e2e8f0" }}>${rec.short_strike} · {formatExpiry(rec.short_expiry)} · {rec.dte} DTE</span></div>
          {isSpread && rec.long_strike && (
            <div>Long: <span style={{ color: "#e2e8f0" }}>${rec.long_strike} · {formatExpiry(rec.long_expiry ?? rec.short_expiry)}</span></div>
          )}
          <div>Spot: <span style={{ color: "#e2e8f0" }}>${fmt(rec.spot_price)}</span></div>
          <div>{isBuy ? "Cost (model)" : "Net Credit (model)"}: <span style={{ color: isBuy ? "#f87171" : "#4ade80" }}>${fmt(rec.net_premium)}</span></div>
        </div>

        {/* Fill fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1 }}>
              CONTRACTS (QUANTITY)
            </span>
            <input
              type="number"
              min="1"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{
                background: "#0d1526", border: "1px solid #1e293b", borderRadius: 8,
                padding: "10px 14px", color: "#e2e8f0", fontSize: 15,
                fontFamily: "'Space Mono', monospace", width: "100%",
              }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1 }}>
              {isBuy ? "PREMIUM PAID (PER SHARE) *" : isSpread ? "SHORT LEG FILL PRICE *" : "PREMIUM RECEIVED (PER SHARE) *"}
            </span>
            <input
              type="number"
              step="0.01"
              value={shortFill}
              onChange={(e) => setShortFill(e.target.value)}
              style={{
                background: "#0d1526", border: "1px solid #1e293b", borderRadius: 8,
                padding: "10px 14px", color: "#e2e8f0", fontSize: 15,
                fontFamily: "'Space Mono', monospace", width: "100%",
              }}
            />
          </label>

          {isSpread && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1 }}>
                LONG LEG FILL PRICE *
              </span>
              <input
                type="number"
                step="0.01"
                value={longFill}
                onChange={(e) => setLongFill(e.target.value)}
                style={{
                  background: "#0d1526", border: "1px solid #1e293b", borderRadius: 8,
                  padding: "10px 14px", color: "#e2e8f0", fontSize: 15,
                  fontFamily: "'Space Mono', monospace", width: "100%",
                }}
              />
            </label>
          )}
        </div>

        {/* Total cost/credit summary */}
        {(() => {
          const qty = parseInt(quantity) || 1;
          const fillPerShare = parseFloat(shortFill) || 0;
          const longFillPerShare = isSpread ? (parseFloat(longFill) || 0) : 0;
          const netPerShare = isSpread ? fillPerShare - longFillPerShare : fillPerShare;
          const totalPerContract = Math.round(netPerShare * 100);
          const totalAll = totalPerContract * qty;
          return (
            <div style={{
              background: "#0d1526", border: "1px solid #1e293b", borderRadius: 10,
              padding: "12px 16px", marginBottom: 20, fontSize: 13, fontFamily: "monospace",
              color: "#94a3b8",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span>{isBuy ? "Cost per contract" : "Credit per contract"}</span>
                <span style={{ color: isBuy ? "#f87171" : "#4ade80", fontFamily: "'Space Mono', monospace" }}>
                  ${totalPerContract.toLocaleString()}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Total ({qty} contract{qty !== 1 ? "s" : ""})</span>
                <span style={{ color: isBuy ? "#f87171" : "#4ade80", fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>
                  ${totalAll.toLocaleString()}
                </span>
              </div>
            </div>
          );
        })()}

        {error && (
          <div style={{
            background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 8,
            padding: "10px 14px", marginBottom: 16, fontSize: 12,
            color: "#f87171", fontFamily: "monospace",
          }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "11px 0", borderRadius: 8,
            border: "1px solid #1e293b", background: "transparent",
            color: "#475569", fontSize: 13, cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}>
            Cancel
          </button>
          <button onClick={handleExecute} disabled={loading} style={{
            flex: 2, padding: "11px 0", borderRadius: 8,
            border: "none", background: loading ? "#1e293b" : "#1d4ed8",
            color: loading ? "#475569" : "#fff", fontSize: 13, fontWeight: 600,
            cursor: loading ? "default" : "pointer",
            fontFamily: "'DM Sans', sans-serif",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            {loading ? (
              <><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Submitting...</>
            ) : (
              <><Zap size={14} /> Confirm Execute</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ─────────────────────────────────────────────────────────────────

function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{
      position: "fixed", bottom: 32, right: 32, zIndex: 2000,
      background: "#052e16", border: "1px solid #166534", borderRadius: 12,
      padding: "14px 20px", display: "flex", alignItems: "center", gap: 10,
      color: "#4ade80", fontSize: 14, fontWeight: 500,
      animation: "slideUp 0.2s ease",
      boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    }}>
      <CheckCircle size={16} />
      {message}
    </div>
  );
}

// ─── RecommendationCard ────────────────────────────────────────────────────

interface CardProps {
  rec: Recommendation;
  onExecute: (rec: Recommendation) => void;
}

function RecommendationCard({ rec, onExecute }: CardProps) {
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const isSpread = rec.long_strike !== null && isSpreadStrategy(rec.strategy);
  const isBuy = isBuyStrategy(rec.strategy);
  const ivHvRatio = rec.hv30 ? (rec.iv_current / rec.hv30).toFixed(2) : null;
  const order = buildRobinhoodOrder(rec);
  const copyText = `${order.instruction}\n${order.detail}`;

  return (
    <div style={{
      background: "#0a1628",
      border: "1px solid #1e293b",
      borderRadius: 14,
      overflow: "hidden",
      animation: "fadeIn 0.3s ease",
      transition: "border-color 0.15s ease",
    }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#334155")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#1e293b")}
    >
      {/* Card top */}
      <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #1e293b" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: "#475569",
              fontFamily: "'Space Mono', monospace", minWidth: 24,
            }}>
              #{rec.rank}
            </span>
            <span style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: "#f1f5f9" }}>
              {rec.ticker}
            </span>
            <span style={{
              padding: "3px 9px", borderRadius: 6, fontSize: 10,
              fontFamily: "monospace", fontWeight: 700, letterSpacing: 0.5,
              ...strategyBadgeStyle(rec.strategy),
            }}>
              {(isSingleLeg(rec.strategy) ? formatStrategyFriendly(rec.strategy) : formatStrategyLabel(rec.strategy)).toUpperCase()}
            </span>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", marginBottom: 3 }}>SCORE</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Space Mono', monospace", color: "#facc15" }}>
              {rec.score_total.toFixed(1)}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 10, fontSize: 13, color: "#64748b", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>
            Spot: <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>
              ${fmt(rec.spot_price)}
            </span>
          </span>
          {rec.ah_change != null && rec.ah_change !== 0 && (
            <span style={{
              fontSize: 10,
              fontFamily: "'Space Mono', monospace",
              padding: "2px 7px",
              borderRadius: 5,
              background: rec.ah_change > 0 ? "#052e1620" : "#1a0a0a20",
              border: `1px solid ${rec.ah_change > 0 ? "#16653440" : "#7f1d1d40"}`,
              color: rec.ah_change > 0 ? "#4ade80" : "#f87171",
            }}>
              AH {rec.ah_change > 0 ? "+" : ""}{rec.ah_change.toFixed(2)}%
            </span>
          )}
        </div>
      </div>

      {/* IV / Volatility section */}
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #1e293b" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", width: 56 }}>IV RANK</span>
            <ScoreBar score={rec.iv_rank} />
            <span style={{ fontSize: 13, fontFamily: "'Space Mono', monospace", color: "#e2e8f0" }}>
              {rec.iv_rank.toFixed(0)}
            </span>
          </div>
          <span style={{ fontSize: 12, color: "#64748b", fontFamily: "monospace" }}>
            IV: <span style={{ color: "#94a3b8" }}>{(rec.iv_current * 100).toFixed(1)}%</span>
          </span>
        </div>
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#64748b", fontFamily: "monospace" }}>
          {rec.hv30 && (
            <span>HV30: <span style={{ color: "#94a3b8" }}>{(rec.hv30 * 100).toFixed(1)}%</span></span>
          )}
          {ivHvRatio && (
            <span>IV/HV: <span style={{ color: "#94a3b8" }}>{ivHvRatio}x</span></span>
          )}
        </div>
      </div>

      {/* Sentiment section */}
      {rec.sentiment_label && (
        <div style={{ padding: "12px 20px", borderBottom: "1px solid #1e293b" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", width: 56 }}>MOOD</span>
              <ScoreBar score={rec.score_sentiment ?? 50} />
              <span style={{ fontSize: 13, fontFamily: "'Space Mono', monospace", color: "#e2e8f0" }}>
                {(rec.score_sentiment ?? 50).toFixed(0)}
              </span>
            </div>
            <SentimentBadge label={rec.sentiment_label} />
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#64748b", fontFamily: "monospace", flexWrap: "wrap" }}>
            {rec.sentiment_bullish != null && (
              <span>
                Bull: <span style={{ color: rec.sentiment_bullish > 0.55 ? "#4ade80" : "#94a3b8" }}>
                  {(rec.sentiment_bullish * 100).toFixed(0)}%
                </span>
              </span>
            )}
            {rec.sentiment_bearish != null && (
              <span>
                Bear: <span style={{ color: rec.sentiment_bearish > 0.55 ? "#f87171" : "#94a3b8" }}>
                  {(rec.sentiment_bearish * 100).toFixed(0)}%
                </span>
              </span>
            )}
            {rec.sentiment_buzz != null && rec.sentiment_buzz !== 1.0 && (
              <span>
                Buzz: <span style={{ color: rec.sentiment_buzz > 1.5 ? "#facc15" : "#94a3b8" }}>
                  {rec.sentiment_buzz.toFixed(1)}x
                </span>
              </span>
            )}
            {rec.vix_level != null && (
              <span>
                VIX: <span style={{ color: rec.vix_level > 25 ? "#fb923c" : "#94a3b8" }}>
                  {rec.vix_level.toFixed(0)}
                </span>
              </span>
            )}
            {rec.macro_risk_level != null && (
              <span>
                Macro: <span style={{ color: rec.macro_risk_level >= 7 ? "#f87171" : rec.macro_risk_level >= 4 ? "#facc15" : "#4ade80" }}>
                  {rec.macro_risk_level}/10
                </span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Strike / expiry section */}
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #1e293b" }}>
        <div style={{ fontSize: 13, color: "#94a3b8", fontFamily: "monospace", marginBottom: 10 }}>
          {isSpread ? (
            <span>
              Short <span style={{ color: "#e2e8f0" }}>${rec.short_strike}</span>
              {" / "}
              Long <span style={{ color: "#e2e8f0" }}>${rec.long_strike}</span>
              {" · "}
              <span style={{ color: "#60a5fa" }}>{formatExpiry(rec.short_expiry)}</span>
              {" · "}
              <span style={{ color: "#475569" }}>{rec.dte} DTE</span>
            </span>
          ) : (
            <span>
              {isBuy ? "" : "Sell "}
              <span style={{ color: "#e2e8f0" }}>${rec.short_strike} {rec.strategy.includes("put") ? "Put" : "Call"}</span>
              {" · "}
              <span style={{ color: "#60a5fa" }}>{formatExpiry(rec.short_expiry)}</span>
              {" · "}
              <span style={{ color: "#475569" }}>{rec.dte} DTE</span>
            </span>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: 12 }}>
          {[
            isBuy
              ? { label: "Cost", value: `$${fmt(rec.net_premium)} ($${(rec.net_premium * 100).toFixed(0)}/contract)`, color: "#f87171" }
              : { label: "Net Credit", value: `$${fmt(rec.net_premium)} ($${(rec.net_premium * 100).toFixed(0)}/contract)`, color: "#4ade80" },
            { label: "Max Loss", value: `$${fmt(rec.max_loss, 0)}`, color: "#f87171" },
            { label: "Breakeven", value: `$${fmt(rec.breakeven)}`, color: "#e2e8f0" },
            { label: "PoP", value: `${rec.pop.toFixed(1)}%`, color: "#60a5fa" },
            { label: "R/R", value: `${rec.risk_reward.toFixed(2)}x`, color: "#facc15" },
            { label: "Max Profit", value: `$${fmt(rec.max_profit, 0)}`, color: "#4ade80" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ color: "#475569", fontFamily: "monospace", fontSize: 10 }}>{label}:</span>
              <span style={{ color, fontFamily: "'Space Mono', monospace", fontSize: 12 }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Robinhood order */}
      <div style={{ padding: "12px 20px", borderBottom: "1px solid #1e293b", background: "#060e1c" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: "#334155", letterSpacing: 1, fontFamily: "monospace", marginBottom: 4 }}>
              ROBINHOOD ORDER
            </div>
            <div style={{ fontSize: 11, color: "#60a5fa", fontFamily: "'Space Mono', monospace", lineHeight: 1.6 }}>
              {order.instruction}
            </div>
            <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", marginTop: 2 }}>
              {order.detail}
            </div>
          </div>
          <CopyButton text={copyText} />
        </div>
      </div>

      {/* Analysis panel */}
      {analysis && (
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid #1e293b",
          background: "#060e1c", fontSize: 12, fontFamily: "monospace",
          color: "#94a3b8", lineHeight: 1.7, whiteSpace: "pre-wrap",
        }}>
          {analysis}
        </div>
      )}
      {analysisError && (
        <div style={{
          padding: "10px 20px", borderBottom: "1px solid #1e293b",
          background: "#1a0a0a", fontSize: 12, fontFamily: "monospace", color: "#f87171",
        }}>
          {analysisError}
        </div>
      )}

      {/* Actions */}
      <div style={{
        padding: "12px 20px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <button
          disabled={analyzing}
          onClick={async () => {
            if (analysis) { setAnalysis(null); return; }
            setAnalyzing(true);
            setAnalysisError(null);
            try {
              const hvRatio = rec.hv30 ? rec.iv_current / rec.hv30 : 1;
              const result = await analyzeOption({
                ticker: rec.ticker,
                stockName: rec.ticker,
                price: rec.spot_price,
                sector: "N/A",
                iv: Math.round(rec.iv_current * 100),
                ivRank: Math.round(rec.iv_rank),
                hvRatio: parseFloat(hvRatio.toFixed(2)),
                strategy: rec.strategy,
                option: {
                  strike: rec.short_strike,
                  premium: rec.net_premium.toFixed(2),
                  annualYield: ((rec.net_premium / rec.spot_price) * (365 / rec.dte) * 100).toFixed(1),
                  probProfit: rec.pop.toFixed(1),
                  dte: rec.dte,
                  delta: (rec.short_delta ?? 0).toFixed(3),
                  theta: (rec.short_theta ?? 0).toFixed(3),
                  breakeven: rec.breakeven.toFixed(2),
                } as OptionLeg & { strike: number },
              });
              setAnalysis(result);
            } catch (e) {
              setAnalysisError(e instanceof Error ? e.message : "Analysis failed");
            } finally {
              setAnalyzing(false);
            }
          }}
          style={{
            background: "transparent", border: "none",
            color: analyzing ? "#334155" : analysis ? "#60a5fa" : "#475569",
            fontSize: 12, cursor: analyzing ? "default" : "pointer", fontFamily: "monospace",
            display: "flex", alignItems: "center", gap: 4,
          }}
        >
          {analyzing ? (
            <><RefreshCw size={12} style={{ animation: "spin 1s linear infinite" }} /> Analyzing...</>
          ) : analysis ? (
            <>Hide Analysis</>
          ) : (
            <>Analyze <ChevronRight size={12} /></>
          )}
        </button>
        <button
          onClick={() => onExecute(rec)}
          style={{
            padding: "8px 18px", borderRadius: 8,
            border: "1px solid #1d4ed8", background: "#1d4ed815",
            color: "#60a5fa", fontSize: 12, fontWeight: 600,
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            display: "flex", alignItems: "center", gap: 6,
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#1d4ed830")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1d4ed815")}
        >
          <Zap size={12} />
          {isBuy ? "I Bought This" : "I Sold This"}
        </button>
      </div>
    </div>
  );
}

// ─── ScreenerPage ──────────────────────────────────────────────────────────

export default function ScreenerPage() {
  const { isAdmin } = useAuth();
  const [result, setResult] = useState<ScreenResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executeRec, setExecuteRec] = useState<Recommendation | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<"spreads" | "single_leg">("single_leg");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchScreenLatest();
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load options results");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleTrigger() {
    setTriggering(true);
    try {
      await triggerScreen();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to trigger screen");
    } finally {
      setTriggering(false);
    }
  }

  function handleExecuteSuccess() {
    setExecuteRec(null);
    setToast("Trade opened successfully! View it in the Trades tab.");
  }

  return (
    <main style={{ maxWidth: 1440, margin: "0 auto", padding: "32px 40px 60px" }}>

      {/* ── Page header ── */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        marginBottom: 20,
      }}>
        <div>
          <h2 style={{
            fontSize: 22, fontWeight: 800, fontFamily: "'Space Mono', monospace",
            color: "#f1f5f9", marginBottom: 6,
          }}>
            Trade Recommendations
          </h2>
          {result && (
            <div style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>
              Last run: <span style={{ color: "#64748b" }}>{timeAgo(result.run_at)}</span>
              {" · "}
              Run #{result.run_id}
              {" · "}
              <span style={{ color: "#4ade80" }}>{result.recommendations.length} signals</span>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={load} disabled={loading} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "9px 16px", borderRadius: 8, border: "1px solid #1e293b",
            background: "#0d1526", color: "#64748b", cursor: loading ? "default" : "pointer",
            fontSize: 12, fontFamily: "monospace",
          }}>
            <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            {loading ? "LOADING..." : "REFRESH"}
          </button>

          {isAdmin && (
          <button onClick={handleTrigger} disabled={triggering || loading} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "9px 18px", borderRadius: 8, border: "none",
            background: triggering ? "#1e293b" : "#1d4ed8",
            color: triggering ? "#475569" : "#fff",
            cursor: triggering || loading ? "default" : "pointer",
            fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
          }}>
            <Play size={13} />
            {triggering ? "Running..." : "Run Now"}
          </button>
          )}
        </div>
      </div>

      {/* ── Tab switcher ── */}
      <div style={{
        display: "flex", gap: 2, marginBottom: 24,
        background: "#0a1628", borderRadius: 10, padding: 3,
        border: "1px solid #1e293b", width: "fit-content",
      }}>
        {([
          { key: "single_leg" as const, label: "Single Leg" },
          { key: "spreads" as const, label: "Spreads" },
        ]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: tab === key ? "#1d4ed8" : "transparent",
              color: tab === key ? "#fff" : "#64748b",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              transition: "all 0.15s ease",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Market Mood Bar ── */}
      {!loading && result && result.recommendations.length > 0 && (() => {
        // Extract market-wide sentiment from the first recommendation that has it
        const firstWithSentiment = result.recommendations.find(r => r.sentiment_label);
        if (!firstWithSentiment) return null;
        const vix = firstWithSentiment.vix_level;
        const macroRisk = firstWithSentiment.macro_risk_level;
        const vixMood = vix == null ? null : vix < 15 ? "Calm" : vix < 20 ? "Normal" : vix < 30 ? "Elevated" : vix < 40 ? "Fear" : "Panic";
        const vixColor = vix == null ? "#94a3b8" : vix < 15 ? "#4ade80" : vix < 20 ? "#94a3b8" : vix < 30 ? "#facc15" : vix < 40 ? "#fb923c" : "#f87171";
        const macroColor = macroRisk == null ? "#94a3b8" : macroRisk <= 3 ? "#4ade80" : macroRisk <= 6 ? "#facc15" : "#f87171";

        // Count earnings tickers
        const earningsTickers = result.recommendations
          .filter(r => r.earnings_within_dte && r.earnings_within_dte > 0)
          .map(r => r.ticker)
          .filter((t, i, arr) => arr.indexOf(t) === i);

        return (
          <div style={{
            display: "flex", gap: 20, marginBottom: 20, padding: "12px 20px",
            background: "#0a1628", border: "1px solid #1e293b", borderRadius: 10,
            alignItems: "center", flexWrap: "wrap", fontSize: 12, fontFamily: "monospace",
          }}>
            <span style={{ color: "#475569", letterSpacing: 1, fontSize: 10, fontWeight: 700 }}>MARKET MOOD</span>
            {vix != null && (
              <span style={{ color: "#64748b" }}>
                VIX: <span style={{ color: vixColor, fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>
                  {vix.toFixed(0)} ({vixMood})
                </span>
              </span>
            )}
            {macroRisk != null && (
              <span style={{ color: "#64748b" }}>
                Macro Risk: <span style={{ color: macroColor, fontFamily: "'Space Mono', monospace", fontWeight: 700 }}>
                  {macroRisk}/10
                </span>
              </span>
            )}
            {earningsTickers.length > 0 && (
              <span style={{ color: "#fb923c" }}>
                Earnings: <span style={{ fontFamily: "'Space Mono', monospace" }}>
                  {earningsTickers.join(", ")}
                </span>
              </span>
            )}
          </div>
        );
      })()}

      {/* ── Error ── */}
      {error && (
        <div style={{
          background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 12,
          padding: 20, marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <AlertCircle size={18} color="#f87171" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ color: "#f87171", fontWeight: 600, marginBottom: 4 }}>Error loading options data</div>
            <div style={{ color: "#7f1d1d", fontSize: 12, fontFamily: "monospace" }}>{error}</div>
          </div>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))", gap: 20 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              height: 300, background: "#0a1628", borderRadius: 14,
              border: "1px solid #1e293b", animation: "pulse 1.5s ease infinite",
              opacity: 1 - i * 0.08,
            }} />
          ))}
        </div>
      )}

      {/* ── Recommendation Grid ── */}
      {!loading && result && result.recommendations.length > 0 && (() => {
        const filtered = result.recommendations.filter((rec) =>
          tab === "spreads" ? isSpreadStrategy(rec.strategy) : isSingleLeg(rec.strategy)
        );
        if (filtered.length === 0) {
          return (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#334155" }}>
              <div style={{ fontSize: 42, marginBottom: 12 }}>&#9671;</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 14, color: "#475569" }}>
                No {tab === "spreads" ? "spread" : "single-leg"} recommendations this run
              </div>
              <div style={{ fontSize: 12, color: "#334155", marginTop: 6 }}>
                {tab === "spreads"
                  ? "Spreads require multi-leg approval on Robinhood"
                  : "Try running the options scan again or adjusting filters"}
              </div>
            </div>
          );
        }
        return (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))",
            gap: 20,
          }}>
            {filtered.map((rec) => (
              <RecommendationCard key={rec.id} rec={rec} onExecute={setExecuteRec} />
            ))}
          </div>
        );
      })()}

      {/* ── Empty state ── */}
      {!loading && result && result.recommendations.length === 0 && (
        <div style={{ textAlign: "center", padding: "80px 0", color: "#334155" }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>◈</div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, marginBottom: 8, color: "#475569" }}>
            No recommendations yet
          </div>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 24 }}>
            {isAdmin ? 'Click "Run Now" to execute a new screen.' : "Waiting for the next scheduled screen run."}
          </div>
          {isAdmin && (
          <button onClick={handleTrigger} style={{
            padding: "10px 24px", borderRadius: 8, border: "none",
            background: "#1d4ed8", color: "#fff", fontSize: 13,
            fontWeight: 600, cursor: "pointer",
          }}>
            Run Screen Now
          </button>
          )}
        </div>
      )}

      {!loading && !result && !error && (
        <div style={{ textAlign: "center", padding: "80px 0", color: "#334155" }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>◈</div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, color: "#475569" }}>
            No options runs found
          </div>
        </div>
      )}

      {/* ── Execute Modal ── */}
      {executeRec && (
        <ExecuteModal
          rec={executeRec}
          onClose={() => setExecuteRec(null)}
          onSuccess={handleExecuteSuccess}
        />
      )}

      {/* ── Toast ── */}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </main>
  );
}
